import React, { useEffect, useState } from "react";
import {
  Collections,
  HistoricalReproduction,
  HistoricalSection,
  PreHero,
} from "../../components";
import { Image98 } from "../../assets";
import SEO from "../../shared/components/SEO";
import { reproductionSeo } from "./reproductionSeo";
import {
  ACTIVE_LISTINGS_ALL,
  ALLOWED_COLLECTIONS,
  fetchCollections,
  formatPrice,
  getNFTMetadata,
} from "../../components/Features/Market/Market";
import ChroniclesSection from "../../components/Features/ChroniclesSection/ChroniclesSection";
import RecentlyUpdatedSection from "../../components/Features/RecentlyUpdatedSection/RecentlyUpdatedSection";
import ItemsGridSection from "../../components/Features/ItemsGridSection/ItemsGridSection";
import { useGraphQL } from "../../hooks/useGraphQL";

const gateways = [
  "https://funooni-public.mypinata.cloud/ipfs/",
  "https://ipfs.io/ipfs/",
  "https://gateway.pinata.cloud/ipfs/",
  "https://cloudflare-ipfs.com/ipfs/",
];

const safeImage = (uri, gateway) => {
  if (!uri) return "";

  if (uri.startsWith("ipfs://")) {
    return `${gateway}${uri.replace("ipfs://", "")}`;
  }

  return uri;
};

const fetchIPFSJson = async (uri) => {
  let lastError;

  for (const gateway of gateways) {
    try {
      const url = safeImage(uri, gateway);

      const res = await fetch(url);

      if (!res.ok) throw new Error("Gateway failed");

      return await res.json();
    } catch (err) {
      lastError = err;
    }
  }

  throw lastError;
};

const getSafeImage = async (uri) => {
  if (!uri) return "";

  for (const gateway of gateways) {
    try {
      const url = safeImage(uri, gateway);

      const res = await fetch(url, {
        method: "HEAD",
      });

      if (res.ok) return url;
    } catch {}
  }

  return "";
};

export const resolveImage = async (item) => {
  try {
    if (item?.token?.metadataUri) {
      const json = await fetchIPFSJson(item.token.metadataUri);

      if (json.image) {
        return await getSafeImage(json.image);
      }
    }

    if (item?.token?.collection?.contractURI) {
      const json = await fetchIPFSJson(item.token.collection.contractURI);

      if (json.image) {
        return await getSafeImage(json.image);
      }
    }
  } catch (err) {
    console.log("image resolve error", err);
  }

  return "";
};

const Reproduction = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nftData, setNftData] = useState({});
  const [collectionImages, setCollectionImages] = useState({});
  const { data: dataListings } = useGraphQL(ACTIVE_LISTINGS_ALL);
  const [heroCollections, setHeroCollections] = useState([]);

  useEffect(() => {
    const loadHeroImages = async () => {
      if (!collections?.length) return;

      const result = await Promise.all(
        collections.map(async (col) => {
          try {
            if (!col.contractURI) return col;

            const meta = await fetchIPFSJson(col.contractURI);

            return {
              ...col,
              image: await getSafeImage(meta.image),
            };
          } catch (err) {
            return col;
          }
        }),
      );

      setHeroCollections(result);
    };

    loadHeroImages();
  }, [collections]);

  useEffect(() => {
    const loadCollectionImages = async () => {
      if (!collections?.length) return;

      const result = {};

      await Promise.all(
        collections.map(async (collection) => {
          try {
            if (!collection.contractURI) return;

            const meta = await fetchIPFSJson(collection.contractURI);

            result[collection.id] = await getSafeImage(meta.image);
          } catch (err) {
            console.error("Metadata error", err);
          }
        }),
      );

      setCollectionImages(result);
    };

    loadCollectionImages();
  }, [collections]);

  useEffect(() => {
    const loadNFTs = async () => {
      if (!dataListings?.listings) return;

      const result = {};

      await Promise.all(
        dataListings.listings.map(async (listing) => {
          const meta = await getNFTMetadata(listing.token?.metadataUri);
          result[listing.id] = meta;
        }),
      );

      setNftData(result);
    };

    loadNFTs();
  }, [dataListings]);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await fetchCollections();
        setCollections(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const palestineCollections = heroCollections.filter(
    (col) => !ALLOWED_COLLECTIONS.includes(col.id?.toLowerCase()),
  );

  return (
    <div className="Reproduction">
      <SEO {...reproductionSeo} />
      <PreHero data={palestineCollections} />
      <HistoricalReproduction
        title="About our historical project"
        description={`Funooni presents Palestine Pre-’48 Time Travel—a groundbreaking project that revives a little-known era of Palestinian life before 1948. Through historical advertisements, restored archival photography, and satirical caricatures, the collection captures industries, geographies, events, and influential personalities of the 1930s and 40s.

Sourced from daily magazines, newspapers, and cultural posts, it offers a vivid glimpse into life such as cities, industry, beauty, festivals, education, etc., before Nakba. Preserved on the blockchain, the archive serves researchers, educators, and cultural enthusiasts as a structured record of heritage.
Funooni brings this history to life with black-and-white and colorized visuals, archival photography, and reimagined caricatures as NFTs— merging memory with technology in an immersive cultural journey.`}
        image={Image98}
      />

      <ChroniclesSection />

      <Collections
        title="Recently Updated Collections"
        isLoading={loading}
        loadingText="Loading..."
        emptyText="No collections found"
      />

      <ItemsGridSection
        items={(dataListings?.listings || [])
          .filter((listing) =>
            ALLOWED_COLLECTIONS.includes(
              listing.token?.collection?.id?.toLowerCase(),
            ),
          )
          .map((listing) => ({
            id: listing.id,
            listingId: listing.listingId,

            metadataUri: listing.token?.metadataUri,

            name: nftData[listing.id]?.name || listing.token?.name || "Unknown",

            year: nftData[listing.id]?.attributes?.find(
              (attr) => attr.trait_type === "Year",
            )?.value,

            description:
              nftData[listing.id]?.description ||
              listing.token?.description ||
              "Unknown",

            size: nftData[listing.id]?.attributes?.find(
              (attr) => attr.trait_type === "Size",
            )?.value,

            image: nftData[listing.id]?.image,
            preview: nftData[listing.id]?.preview,

            price: formatPrice(listing.pricePerToken),
            quantity: listing.quantity,

            token: listing.token,

            nft: listing.nft || listing.token?.collection,

            raw: listing,
          }))}
      />
      <HistoricalSection
        title="Cultural Heritage"
        subtitle="A journey through the nation’s cultural heritage"
        cardTitle="PALESTINE"
        cardSubtitle="Cultural Heritage"
        image={Image98}
        description={`Funooni presents Palestine Pre-’48 Time Travel—a groundbreaking project that revives a little-known era of Palestinian life before 1948. Through historical advertisements, restored archival photography, and satirical caricatures, the collection captures industries, geographies, events, and influential personalities of the 1930s and 40s.

Sourced from daily magazines, newspapers, and cultural posts, it offers a vivid glimpse into life such as cities, industry, beauty, festivals, education, etc., before Nakba. Preserved on the blockchain, the archive serves researchers, educators, and cultural enthusiasts as a structured record of heritage.
Funooni brings this history to life with black-and-white and colorized visuals, archival photography, and reimagined caricatures as NFTs— merging memory with technology in an immersive cultural journey.`}
      />

      {!loading && collections?.length > 0 && (
        <RecentlyUpdatedSection
          title="Recently Updated Collections"
          collections={collections.filter(
            (col) => !ALLOWED_COLLECTIONS.includes(col.id?.toLowerCase()),
          )}
        />
      )}
    </div>
  );
};

export default Reproduction;
